// @flow

import * as React from 'react';

import _ from 'lodash';

import {AlignBox} from 'sail/Align';
import Badge from 'sail/Badge';
import {BodyTextProps, Subheading, Text} from 'sail/Text';

import Box from 'sail/Box';
import Card from 'sail/Card';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
} from 'sail/DataTable';
import {ErrorState} from 'sail/Content';
import Group from 'sail/Group';
import Notice from 'sail/Notice';
import Spinner from 'sail/Spinner';
import {TooltipContainer} from 'sail/Tooltip';

import type {ConnectRequiredInfoSelection} from './ConnectRequiredInfo';

export type FieldRequirementDatum = {|
  capability_limit_amount: ?number,
  capability_limit_time: ?number,
  payment_limit_amount: ?number,
  payment_limit_time: ?number,
  payout_limit_amount: ?number,
  payout_limit_time: ?number,
  trigger_limit_amount: ?number,

  verified_capability_limit_amount: ?number,
  verified_capability_limit_time: ?number,
  verified_payment_limit_amount: ?number,
  verified_payment_limit_time: ?number,
  verified_payout_limit_amount: ?number,
  verified_payout_limit_time: ?number,
  verified_trigger_limit_amount: ?number,

  can_receive_payouts_and_settle_charges: boolean,
  alternatives: Array<string>,

  sg_cdd_accounts_only: boolean,
|};

export type ConnectRequiredInfoTableProps = {|
  error: boolean,
  loading: boolean,
  reqData: {[string]: FieldRequirementDatum},
  curSelection: ConnectRequiredInfoSelection,
  isLoggedIn: boolean,
|};

// would be great if we could generate this dynamically somehow
const fieldMetadataMap = {
  account: [
    {
      display: 'Business type',
      key_regex: /business_type/,
    },
    {
      display: 'Business structure',
      key_regex: /company\.structure/,
    },
    {
      display: 'Merchant category code',
      key_regex: /business_profile.mcc/,
    },
    {
      display: 'URL',
      key_regex: /business_profile.(url|requirement)/,
    },
    {
      display: 'Product description',
      key_regex: /business_profile.product_description/,
    },
    {
      display: 'Support phone',
      key_regex: /business_profile.support_phone/,
    },
    {
      display: 'Statement descriptors',
      key_regex: /settings.payments.statement_descriptor/,
    },
    {
      display: 'Konbini support email address',
      key_regex: /settings.konbini_payments.support_email/,
    },
    {
      display: 'Konbini support phone number',
      key_regex: /settings.konbini_payments.support_phone/,
    },
    {
      display: 'Konbini support hours',
      key_regex: /settings.konbini_payments.support_hours/,
    },
    {
      display: 'Terms of service',
      key_regex: /tos_acceptance/,
    },
    {
      // This is special-cased below as blocking payouts only.
      display: 'External account',
      key_regex: /external_account/,
    },
  ],
  entity: [
    {
      display: 'Company name',
      key_regex: /name$/,
      extra: {
        JP: '会社名 (ローマ字または英語表記)',
      },
    },
    {
      display: 'Company name (kana)',
      key_regex: /name_kana/,
      extra: {
        JP: '会社名 (かな表記)',
      },
    },
    {
      display: 'Company name (kanji)',
      key_regex: /name_kanji/,
      extra: {
        JP: '会社名 (漢字表記)',
      },
    },
    {
      display: 'Company address',
      key_regex: /address\..*/,
      extra: {
        HK: 'PO boxes are not allowed',
        JP: '会社の住所 (かな表記)',
        US: 'Must be a U.S. address. PO Boxes are not allowed.',
      },
    },
    {
      display: 'Company address (kana)',
      key_regex: /address_kana/,
      extra: {
        JP: '会社の住所 (かな表記)',
      },
    },
    {
      display: 'Company address (kanji)',
      key_regex: /address_kanji/,
      extra: {
        JP: '会社の住所 (漢字表記)',
      },
    },
    {
      display: 'Company phone',
      key_regex: /phone/,
      extra: {
        JP: '会社の電話番号',
        US: 'Must be a U.S. phone number.',
      },
    },
    {
      display: 'Company tax ID',
      key_regex: /tax_id/,
      extra: {
        AE: 'Company Registration Number',
        AR: 'Clave Única de Identificación Tributaria (CUIT) - 11 digit number',
        AU: 'Australian Business Number (ABN)',
        BO: 'Número de Identificación Tributaria (NIT) - 7 to 15 digit number',
        BR: 'Cadastro Nacional da Pessoa Jurídica (CNPJ) - 14 digit number',
        CA: 'Business Number (nine-digit tax ID)',
        CL: 'Rol Único Tributario (RUT) - 9 digit number',
        CO: 'Número de Identificación Tributaria (NIT) - 10 digit number',
        HK: 'Business Registration Number (商業登記證號碼)',
        JP: '登記番号 (会社法人等番号)',
        KE: 'Personal Identification Number (KRA PIN)',
        MX: 'Registro Federal de Contribuyentes (RFC) number',
        NZ: 'New Zealand Business Number (NZBN)',
        PH:
          'Philippines Taxpayer Identification Number (TIN) - 12 digit number',
        PY: 'Registro Único de Contribuyentes (RUC) - 7 to 9 digit number',
        SA: 'Saudi Arabia Tax Identification Number (TIN) - 10 digits',
        SG: 'Unique Entity Number (UEN)',
        TR: 'Turkey Tax Identification Number (TIN) - 10 digit number',
      },
    },
    {
      display: 'Company registration number',
      key_regex: /registration_number/,
      extra: {
        HK: 'Company Registration Number (公司註冊證書編號)',
        AU: 'Australian Company Number (ACN)',
      },
    },
    {
      display: 'Company ID number',
      key_regex: /id_number/,
      extra: {
        HK: 'Company Registration Number',
      },
    },
    {
      display: 'Trade license',
      key_regex: /company_license/,
    },
    {
      display: 'Memorandum of Association',
      key_regex: /company_memorandum_of_association/,
    },
    {
      display: 'Proof of bank account',
      key_regex: /bank_account_ownership_verification/,
    },
    {
      display: 'Executives provided',
      key_regex: /executives_provided/,
    },
    {
      display: 'Owners provided', // TODO: clarify this is needed
      key_regex: /owners_provided/,
    },
  ],
  person: [
    {
      display: 'Name',
      key_regex: /(first|last)_name/,
      extra: {
        MX:
          'Legal name as it appears on the Registro Federal de Contribuyentes (RFC) record',
      },
    },
    {
      display: 'Name (kana)',
      key_regex: /(first|last)_name_kana/,
      extra: {
        JP: '氏名 (かな表記)',
      },
    },
    {
      display: 'Name (kanji)',
      key_regex: /(first|last)_name_kanji/,
      extra: {
        JP: '氏名 (漢字表記)',
      },
    },
    {
      display: 'Aliases',
      key_regex: /full_name_aliases/,
    },
    {
      display: 'Date of birth',
      key_regex: /dob\./,
    },
    {
      display: 'Address',
      key_regex: /address\./,
      extra: {
        US: 'Must be a U.S. address. PO Boxes are not allowed.',
      },
    },
    {
      display: 'Address (kana)',
      key_regex: /address_kana/,
      extra: {
        JP: '住所 (かな表記)',
      },
    },
    {
      display: 'Address (kanji)',
      key_regex: /address_kanji/,
      extra: {
        JP: '住所 (漢字表記)',
      },
    },
    {
      display: 'Email',
      key_regex: /email/,
    },
    {
      display: 'Phone',
      key_regex: /phone/,
      extra: {
        US: 'Must be a U.S. phone number.',
      },
    },
    {
      display: 'Gender',
      key_regex: /gender/,
      extra: {
        JP: '性別',
      },
    },
    {
      display: 'Political Exposure',
      key_regex: /political_exposure/,
    },
    {
      display: 'Tax information',
      key_regex: /(ssn_last_4|id_number)/,
      extra: {
        AE:
          'Emirates ID (EID) - 15 digit number. For non-UAE nationals a passport number is acceptable',
        AR: 'Código Único de Identificación Laboral (CUIL) - 11 digit number',
        BO: 'Cédula de Identidad (CI) - 7 or 8 digit number',
        BR: 'Cadastro de Pessoas Físicas (CPF) - 11 digit number',
        CL: 'Rol Único Tributario (RUT) - 9 digit number',
        CO: 'Cédula de Ciudadanía (CC) - 7 to 10 digit number',
        HK: 'Hong Kong Identity Card (HKID) number',
        PY: 'Cédula de identidad civil (CI) - 7 digit number',
        SG:
          'National Registration Identity Card (NRIC) number or Foreign Identification Number (FIN)',
      },
    },
    {
      display: 'Relationship with company',
      key_regex: /relationship\./,
    },
    {
      display: 'Nationality',
      key_regex: /nationality/,
    },
    {
      display: 'Passport',
      key_regex: /passport/,
    },
  ],
};

export const ConnectRequiredInfoTable = (
  props: ConnectRequiredInfoTableProps,
) => {
  const {error, loading, curSelection, isLoggedIn} = props;
  const {country} = curSelection;
  let {reqData} = props;
  const firstAction =
    curSelection.capabilityTypeSet.has('transfers') &&
    !curSelection.capabilityTypeSet.has('card_payments')
      ? 'First transfer'
      : 'First charge';

  const columns = [
    {width: 200},
    {label: 'Required before', width: 250},
    {label: 'Parameter'},
  ];
  const loadingSpinner = <Spinner text="loading requirements..." />;
  const errorMessage = (
    <ErrorState size="medium" title="Requirements list failed to load" />
  );

  // inject business_type and company structure requirement
  const blankRequirements = {
    capability_limit_amount: null,
    capability_limit_time: null,
    payment_limit_amount: null,
    payment_limit_time: null,
    payout_limit_amount: null,
    payout_limit_time: null,
    trigger_limit_amount: null,

    verified_capability_limit_amount: null,
    verified_capability_limit_time: null,
    verified_payment_limit_amount: null,
    verified_payment_limit_time: null,
    verified_payout_limit_amount: null,
    verified_payout_limit_time: null,
    verified_trigger_limit_amount: null,

    can_receive_payouts_and_settle_charges: true,
  };
  reqData = {
    ...reqData,
    [`business_type=${curSelection.entityType}`]: blankRequirements,
    ...(curSelection.businessStructure
      ? {
          [`company.structure=${curSelection.businessStructure}`]: blankRequirements,
        }
      : null),
  };

  const reqTable = () => {
    const titleRow = (title) => {
      return (
        <DataTableRow background="offset" key={title}>
          <DataTableCell>
            <Subheading>
              <em>{title}</em>
            </Subheading>
          </DataTableCell>
          <DataTableCell />
          <DataTableCell />
        </DataTableRow>
      );
    };

    const getRequirements = (
      displayName: string,
      reqs: FieldRequirementDatum,
    ) => {
      const shouldHidePaymentRequirement: boolean =
        displayName === 'External account';

      // Convert cents to dollars and add thousands separator
      // all capability limits are in USD. However, the is a special case,
      // when we see 2770 USD, we actually mean 2500 EUR and only show when
      // logged in.
      const displayCurrency = (amount: number): string => {
        const amt = amount / 100;

        if (amt === 2770) {
          return isLoggedIn ? '2,500 EUR' : '';
        } else {
          return `${amt
            .toString()
            .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')} USD`;
        }
      };

      const minNotNull = (nums: Array<?number>): ?number => {
        const filtered = nums.filter((n) => n === 0 || n);
        return filtered.length > 0 ? _.min(filtered) : null;
      };

      const displayRequirement = (
        pvAmount: ?number,
        timeAmount: ?number,
        beforeFirst: string,
      ): ?string => {
        let pv = null;
        let time = null;
        if (pvAmount == null && timeAmount == null) {
          return null;
        } else if (
          (pvAmount && pvAmount <= 0) ||
          (timeAmount && timeAmount <= 0)
        ) {
          return beforeFirst;
        } else {
          pv = pvAmount && pvAmount > 0 ? displayCurrency(pvAmount) : null;
          time = timeAmount && timeAmount > 0 ? `${timeAmount} days` : null;

          // if there is PV and time limits, display them side-by-side.
          if (pv && time) {
            return `${pv}/${time}`;
          } else if (pv) {
            return pv;
          } else if (time) {
            return time;
          }
          return null;
        }
      };

      const lines: Map<string, Set<string>> = new Map();

      // to get around a very annoying flow issue
      const addToLines = (line: string, text: string): void => {
        const myLine = lines.get(line);
        if (myLine) {
          myLine.add(text);
        }
      };

      const paymentLine = displayRequirement(
        minNotNull([reqs.payment_limit_amount, reqs.capability_limit_amount]),
        minNotNull([reqs.payment_limit_time, reqs.capability_limit_time]),
        firstAction,
      );
      if (paymentLine && !shouldHidePaymentRequirement) {
        if (!lines.get(paymentLine)) {
          lines.set(paymentLine, new Set());
        }
      }

      const verifiedPaymentLine = displayRequirement(
        minNotNull([
          reqs.verified_payment_limit_amount,
          reqs.verified_capability_limit_amount,
        ]),
        minNotNull([
          reqs.verified_payment_limit_time,
          reqs.verified_capability_limit_time,
        ]),
        firstAction,
      );
      if (verifiedPaymentLine && !shouldHidePaymentRequirement) {
        if (!lines.get(verifiedPaymentLine)) {
          lines.set(verifiedPaymentLine, new Set());
        }
        addToLines(verifiedPaymentLine, 'verified');
      }

      const payoutLine = displayRequirement(
        reqs.payout_limit_amount,
        reqs.payout_limit_time,
        'First payout',
      );
      if (payoutLine) {
        if (!lines.get(payoutLine)) {
          lines.set(payoutLine, new Set());
        }
        if (payoutLine !== 'First payout') {
          addToLines(payoutLine, 'payouts');
        }
      }

      const verifiedPayoutLine = displayRequirement(
        reqs.verified_payout_limit_amount,
        reqs.verified_payout_limit_time,
        'First payout',
      );
      if (verifiedPayoutLine) {
        if (!lines.get(verifiedPayoutLine)) {
          lines.set(verifiedPayoutLine, new Set());
        }
        addToLines(verifiedPayoutLine, 'verified');
        if (payoutLine !== 'First payout') {
          addToLines(verifiedPayoutLine, 'payouts');
        }
      }

      const verifiedBadge = (
        <TooltipContainer
          contentPadding={12}
          context={<Badge color="blue" label="Verified" />}
          tooltipContents="Verification of this field required before this threshold"
        />
      );

      const payoutsBadge = (
        <TooltipContainer
          contentPadding={12}
          context={<Badge color="blue" label="Payouts" />}
          tooltipContents="Required before this threshold to keep payouts enabled"
        />
      );

      const hydratedLines = [];
      lines.forEach((tags, req) => {
        const line = [req];
        tags.forEach((tag) => {
          switch (tag) {
            case 'verified':
              line.push(verifiedBadge);
              break;
            case 'payouts':
              line.push(payoutsBadge);
              break;
          }
        });
        hydratedLines.push(line);
      });

      return (
        <Group wrap="wrap" direction="vertical" spacing={{vertical: 12}}>
          {hydratedLines.map((line, i) => (
            <Group
              wrap="wrap"
              direction="horizontal"
              spacing={{horizontal: 4}}
              key={i}
            >
              {line.map((item, j) => (
                <AlignBox reference={BodyTextProps} key={j}>
                  {item}
                </AlignBox>
              ))}
            </Group>
          ))}
        </Group>
      );
    };

    // TODO(booi) if different requirements, should output multiple rows
    const dataRow = (entryGroup, prefix = '') => {
      const fields = [];
      let reqMap;

      Object.keys(reqData).forEach((fieldName) => {
        if (
          fieldName.startsWith(prefix) &&
          entryGroup.key_regex.test(
            prefix ? fieldName.substring(prefix.length + 1) : fieldName,
          )
        ) {
          fields.push(fieldName);
          reqMap = reqData[fieldName];
        }
      });

      const joinedFields = fields.flatMap((field, idx, arr) => {
        return arr.length - 1 !== idx ? (
          <div key={idx}>
            <code>{field}</code>
            <br />
          </div>
        ) : (
          <code key={idx}>{field}</code>
        );
      });

      let formattedAlternatives;
      if (reqMap && reqMap.alternatives && reqMap.alternatives.length > 0) {
        const alternatives = reqMap.alternatives;
        if (alternatives.length === 1) {
          formattedAlternatives = (
            <Text typeface="mono">{alternatives[0]}</Text>
          );
        } else if (alternatives.length === 2) {
          formattedAlternatives = (
            <Text>
              <Text typeface="mono">{alternatives[0]}</Text> and{' '}
              <Text typeface="mono">{alternatives[1]}</Text>
            </Text>
          );
        } else {
          const lastAlternative = alternatives.pop();
          formattedAlternatives = alternatives.map((alternative) => (
            <Text>
              <Text typeface="mono">{alternative}</Text>,{' '}
            </Text>
          ));
          formattedAlternatives.push(
            <Text>
              and <Text typeface="mono">{lastAlternative}</Text>
            </Text>,
          );
        }
      }

      return fields.length === 0 ? null : (
        <DataTableRow key={`${prefix}-${entryGroup.display}`}>
          <DataTableCell>{entryGroup.display}</DataTableCell>
          <DataTableCell>
            {reqMap && getRequirements(entryGroup.display, reqMap)}
          </DataTableCell>
          <DataTableCell>
            {joinedFields}
            {entryGroup?.extra && entryGroup.extra[country] && (
              <Notice color="offset" description={entryGroup.extra[country]} />
            )}
            {reqMap?.sg_cdd_accounts_only && (
              <Notice
                color="offset"
                description={
                  <Text>
                    This requirement applies only to accounts subject to
                    enhanced identity verification.
                  </Text>
                }
              />
            )}
            {formattedAlternatives && (
              <Notice
                color="offset"
                description={
                  <Box>
                    <Text>
                      If the user cannot provide their{' '}
                      {entryGroup.display.toLowerCase()}, you can provide{' '}
                    </Text>
                    {formattedAlternatives}
                    <Text> instead.</Text>
                  </Box>
                }
              />
            )}
          </DataTableCell>
        </DataTableRow>
      );
    };

    const dataGroup = (groupId, prefix) => {
      const rows = [];

      fieldMetadataMap[groupId].forEach((entryGroup) => {
        const row = dataRow(entryGroup, prefix);
        if (row) {
          rows.push(row);
        }
      });
      return rows;
    };

    const rows = [];
    rows.push(titleRow('Account information'));
    rows.push(dataGroup('account'));

    const entityRows = dataGroup('entity', 'company');
    if (entityRows.length > 0) {
      rows.push(titleRow('Company'));
      rows.push(...entityRows);
    }

    const documentRows = dataGroup('entity', 'documents');
    if (documentRows.length > 0) {
      rows.push(titleRow('Documents'));
      rows.push(...documentRows);
    }

    const individualRows = dataGroup('person', 'individual');
    if (individualRows.length > 0) {
      rows.push(titleRow('Individual'));
      rows.push(...individualRows);
    }

    const representativeRows = dataGroup('person', 'representative');
    if (representativeRows.length > 0) {
      rows.push(titleRow('Representative'));
      rows.push(...representativeRows);
    }

    const directorRows = dataGroup('person', 'director');
    if (directorRows.length > 0) {
      rows.push(titleRow('Director'));
      rows.push(...directorRows);
    }

    const ownerRows = dataGroup('person', 'owner');
    if (ownerRows.length > 0) {
      rows.push(titleRow('Owner'));
      rows.push(...ownerRows);
    }

    const executiveRows = dataGroup('person', 'executive');
    if (executiveRows.length > 0) {
      rows.push(titleRow('Executive'));
      rows.push(...executiveRows);
    }

    return rows;
  };

  const getBody = () => {
    if (loading) {
      return (
        <DataTableRow>
          <DataTableCell colSpan={columns.length}>
            <Box margin={{all: 64}}>{loadingSpinner}</Box>
          </DataTableCell>
        </DataTableRow>
      );
    } else if (error) {
      return (
        <DataTableRow>
          <DataTableCell colSpan={columns.length}>
            <Box margin={{all: 64}}>{errorMessage}</Box>
          </DataTableCell>
        </DataTableRow>
      );
    } else {
      return reqTable();
    }
  };

  return (
    <Box margin={{top: 20}}>
      <Card shadow="keyline">
        <DataTable>
          <DataTableHead columns={columns} />
          <DataTableBody>{getBody()}</DataTableBody>
        </DataTable>
      </Card>
    </Box>
  );
};
